import React from 'react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { TEMPERATURE_LABELS } from '../constants';
import type {
  ProspectTemperature,
  CreateLeadInput,
  ExtractedLeadFields,
} from '../types';
import type { CaptureMethod } from '../types';

const TEMPERATURE_OPTIONS = Object.entries(TEMPERATURE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export interface LeadFormValues
  extends Omit<CreateLeadInput, 'stakeholder_type' | 'capture_method'> {
  name: string;
  email: string;
  phone: string;
  company: string;
  job_title: string;
  notes: string;
  prospect_temperature: ProspectTemperature;
}

interface LeadFormProps {
  stakeholderTypeId: number;
  stakeholderTypeLabel: string;
  captureMethod: CaptureMethod;
  initialValues?: Partial<LeadFormValues> | ExtractedLeadFields;
  onSubmit: (values: CreateLeadInput) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
}

const defaultFormValues: LeadFormValues = {
  prospect_temperature: 'warm',
  name: '',
  email: '',
  phone: '',
  company: '',
  job_title: '',
  notes: '',
};

export function LeadForm({
  stakeholderTypeId,
  stakeholderTypeLabel,
  captureMethod,
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save Lead',
}: LeadFormProps) {
  const [values, setValues] = React.useState<LeadFormValues>({
    ...defaultFormValues,
    ...initialValues,
  });

  const handleChange = (field: keyof LeadFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field: 'prospect_temperature') => (value: string) => {
    setValues((prev) => ({ ...prev, [field]: value as ProspectTemperature }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      stakeholder_type: stakeholderTypeId,
      capture_method: captureMethod,
      prospect_temperature: values.prospect_temperature,
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim() || undefined,
      company: values.company.trim() || undefined,
      job_title: values.job_title.trim() || undefined,
      notes: values.notes.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contact details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Prospect temperature</Label>
            <Select
              value={values.prospect_temperature}
              onValueChange={handleSelectChange('prospect_temperature')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select temperature" />
              </SelectTrigger>
              <SelectContent>
                {TEMPERATURE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Stakeholder type</Label>
            <Input value={stakeholderTypeLabel} disabled className="bg-muted" />
          </div>

          <div>
            <Label htmlFor="lead-name">Name *</Label>
            <Input
              id="lead-name"
              value={values.name}
              onChange={handleChange('name')}
              placeholder="Full name"
              required
            />
          </div>

          <div>
            <Label htmlFor="lead-email">Email *</Label>
            <Input
              id="lead-email"
              type="email"
              value={values.email}
              onChange={handleChange('email')}
              placeholder="email@example.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="lead-phone">Phone</Label>
            <Input
              id="lead-phone"
              value={values.phone}
              onChange={handleChange('phone')}
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <Label htmlFor="lead-company">Company</Label>
            <Input
              id="lead-company"
              value={values.company}
              onChange={handleChange('company')}
              placeholder="Company name"
            />
          </div>

          <div>
            <Label htmlFor="lead-job-title">Job title / role</Label>
            <Input
              id="lead-job-title"
              value={values.job_title}
              onChange={handleChange('job_title')}
              placeholder="Senior Property Manager"
            />
          </div>

          <div>
            <Label htmlFor="lead-notes">Notes</Label>
            <Textarea
              id="lead-notes"
              value={values.notes}
              onChange={handleChange('notes')}
              placeholder="Where you met, context, follow-up notes..."
              rows={3}
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={!values.name.trim() || !values.email.trim() || isSubmitting}>
              {isSubmitting ? 'Saving...' : submitLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
